import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canonicalBisApplies, bisAudienceLabel, bisChannelLabel, bisPieceLabel, BIS_BLOCK_LABELS, BIS_DEFAULT_CLASS, BIS_INFO_CLASS_LABELS, BIS_SECTIONS, BIS_SECTION_HINTS, BIS_SECTION_LABELS, type AssetKind, type BisForce, type BisVersionLevel, type BisRuleKind, type BisSection } from '@raro/domain';
import { Button, cx, Label, Notice, Segmented, Serif, Tag, Textarea, Thinking, Toggle } from '@raro/ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { BisAssetsContext, type BisAsset } from '../../components/bis/assets';
import { BisAudiencesContext, BisProgramsContext, BlockEditor, blankBlock, draftOf, sendBlock, type BlockDraft } from '../../components/bis/BlockEditor';
import { BlockView, missingFields } from '../../components/bis/BlockView';
import { ConstructionRoom } from '../../components/bis/ConstructionRoom';
import { AssetsRoom } from '../../components/bis/AssetsRoom';
import { BoardReader } from '../../components/bis/BoardReader';
import { MoldRooms } from '../../components/bis/MoldRooms';
import { ReferenceRoom, refState, type RefRow } from '../../components/bis/ReferenceRoom';
import { PublishLevel, publishLabel } from '../../components/bis/PublishLevel';
import { BIS_STATE_LABELS, bisV, plural, type BisData, type BisItem } from '../../components/bis/types';
import { MachineFill } from '../../components/MachineFill';
import { api } from '../../lib/api';
import { uploadDirect } from '../../lib/upload';
import { formatDate } from '../../lib/format';

type Mode = 'construcao' | 'ler' | 'construir' | 'moldes' | 'assets';
const num = (n: number) => String(n + 1).padStart(2, '0');
/** "onde vale" de um bloco, já com os nomes de tela (canal, tipo de peça, público). O nome de um público vem do bloco dele. */
let audienceTitles = new Map<string, string>();
const appliesOf = (i: BisItem) => { const a = canonicalBisApplies(i.applies).applies; return a ? [...(a.programs ?? []).map((k) => `só ${audienceTitles.get(k) ?? 'um programa'}`), ...a.channels.map(bisChannelLabel), ...a.pieces.map(bisPieceLabel), ...a.audiences.map((k) => bisAudienceLabel(k, (id) => audienceTitles.get(id)))] : []; };

/**
 * O BIS em tela cheia, com quatro modos (o quarto, **assets**, é a sala em que a Raro faz e refina os ativos visuais: ver `AssetsRoom`). **O livro** é como se lê: capítulos, cada bloco com o desenho
 * do seu tipo, filtro por onde vale. **Moldes** é a sala em que o designer transforma os arquivos finais de uma peça no
 * molde que o MODO preenche (só quem edita o BIS). **Construir** é onde a Raro revisa o que a Machine propôs: a estrutura com o estado de
 * cada capítulo, os blocos, e o bloco escolhido aberto para editar. Ninguém preenche o BIS do zero aqui: ele sai do que
 * entra no cliente. O que o MODO e o Brand Audit leem é a versão publicada.
 */
export function BisPage() {
  const { id = '' } = useParams();
  const [sp, setSp] = useSearchParams();
  const brandId = sp.get('marca') ?? undefined;
  const qs = brandId ? `?brandId=${brandId}` : '';
  const qc = useQueryClient();
  const key = ['bis', id, brandId ?? ''];
  const org = useQuery({ queryKey: ['organization', id], queryFn: () => api.get<{ name: string }>(`/organizations/${id}`) });
  const brands = useQuery({ queryKey: ['brands', id], queryFn: () => api.get<{ id: string; name: string }[]>(`/organizations/${id}/brands`), enabled: Boolean(brandId) });
  const q = useQuery({ queryKey: key, queryFn: () => api.get<BisData>(`/organizations/${id}/bis${qs}`), refetchInterval: (x) => (x.state.data?.draft.status === 'pending' ? 3000 : false) });
  const d = q.data;
  const audiences = useMemo(() => (d?.items ?? []).filter((i) => i.kind === 'audience').map((i) => ({ id: i.id, title: i.title })), [d?.items]);
  const programs = useMemo(() => (d?.items ?? []).filter((i) => i.kind === 'program').map((i) => ({ id: i.id, title: i.title })), [d?.items]);
  // os nomes de público e de programa numa tabela só: os dois são blocos deste BIS, citados pelo id
  audienceTitles = new Map([...audiences, ...programs].map((a) => [a.id, a.title]));
  const name = (brandId && brands.data?.find((b) => b.id === brandId)?.name) || org.data?.name || '';
  const hasActive = Boolean(d?.items.some((i) => i.status === 'active'));
  const modo = sp.get('modo') === 'criacao' ? 'assets' : sp.get('modo'); // o endereço antigo da sala de assets continua abrindo
  const mode: Mode = ['construcao', 'ler', 'construir'].includes(modo ?? '') || ((modo === 'moldes' || modo === 'assets') && d?.canEdit !== false) ? (modo as Mode) : hasActive ? 'ler' : d?.canEdit && !d.items.length ? 'construcao' : 'construir';
  const setMode = (m: Mode) => { const n = new URLSearchParams(sp); n.set('modo', m); setSp(n, { replace: true }); };
  const done = () => void qc.invalidateQueries({ queryKey: key });
  // arquivos subidos nesta sessão, antes de o bloco ser salvo: a tela já mostra a imagem
  const [fresh, setFresh] = useState<Record<string, BisAsset>>({});
  const lookup = useCallback((assetId: string) => fresh[assetId] ?? d?.assets[assetId], [fresh, d?.assets]);
  const upload = useCallback(async (file: File, kind: AssetKind) => {
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    if (brandId) form.append('brandId', brandId);
    const a = await api.post<{ id: string; title: string; kind: AssetKind; image: { url: string; thumbUrl: string } | null }>(`/organizations/${id}/assets`, form);
    setFresh((f) => ({ ...f, [a.id]: { title: a.title, kind: a.kind, url: a.image?.url ?? null, thumbUrl: a.image?.thumbUrl ?? null } }));
    return { assetId: a.id, label: file.name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ') };
  }, [id, brandId]);

  return (
    <BisAssetsContext.Provider value={{ lookup, upload: d?.canEdit ? upload : undefined }}>
    <BisAudiencesContext.Provider value={audiences}>
    <BisProgramsContext.Provider value={programs}>
    <div className="rr-bisfull">
      <header className="rr-bisfull__bar">
        <div className="rr-bisfull__where">
          <Link to={`/clientes/${id}?aba=bis${brandId ? `&marca=${brandId}` : ''}`} className="rr-action rr-action--bare">← {org.data?.name ?? 'cliente'}</Link>
          <span className="rr-bisfull__name"><b>BIS™</b> {name}<span className="rr-bis__version">{d?.version ? `v${bisV(d)}` : 'sem versão'}</span></span>
        </div>
        <Segmented options={[{ value: 'construcao', label: 'construção' }, { value: 'construir', label: 'blocos' }, { value: 'ler', label: 'o livro' }, ...(d?.canEdit ? [{ value: 'moldes', label: 'moldes' }, { value: 'assets', label: 'assets' }] : [])]} value={mode} onChange={(m) => setMode(m as Mode)} />
        <span className="rr-bisfull__state">
          {d ? [d.pending.proposed && plural(d.pending.proposed, 'proposta para revisar', 'propostas para revisar'), d.pending.total && plural(d.pending.total, 'mudança não publicada', 'mudanças não publicadas')].filter(Boolean).join(' · ') || (d.version ? `publicado em ${formatDate(d.publishedAt!)}` : '') : ''}
        </span>
      </header>
      {!d ? <p className="rr-faint" style={{ padding: 'var(--rr-space-6)' }}>carregando…</p>
        : mode === 'ler' ? <Book d={d} name={name} onBuild={() => setMode('construir')} />
        : mode === 'construcao' ? <ConstructionRoom organizationId={id} brandId={brandId} canEdit={d.canEdit} onBis={done} />
        : mode === 'assets' ? <AssetsRoom organizationId={id} brandId={brandId} items={d.items} onBis={done} onBuild={() => setMode('construir')} />
        : mode === 'moldes' ? <MoldRooms organizationId={id} qs={qs} brandId={brandId} onBis={() => { done(); setMode('construir'); }} />
        : <Builder d={d} organizationId={id} qs={qs} onDone={done} onConstruction={() => setMode('construcao')} doc={sp.get('doc')} setDoc={(v) => { const n = new URLSearchParams(sp); if (v) n.set('doc', v); else n.delete('doc'); setSp(n, { replace: true }); }} />}
    </div>
    </BisProgramsContext.Provider>
    </BisAudiencesContext.Provider>
    </BisAssetsContext.Provider>
  );
}

// ───────────────────────── o livro ─────────────────────────
function Book({ d, name, onBuild }: { d: BisData; name: string; onBuild: () => void }) {
  const active = d.items.filter((i) => i.status === 'active');
  const [withProposals, setWithProposals] = useState(active.length === 0);
  const [where, setWhere] = useState('');
  const shown = (withProposals ? d.items : active).filter((i) => !where || !appliesOf(i).length || appliesOf(i).includes(where));
  const contexts = useMemo(() => [...new Set(d.items.flatMap(appliesOf))].sort(), [d.items]);
  const chapters = BIS_SECTIONS.map((s) => ({ section: s, items: shown.filter((i) => i.section === s) })).filter((c) => c.items.length);
  const proposals = d.items.length - active.length;

  if (!d.items.length) return (
    <div className="rr-bisbook__empty">
      <Serif italic>o BIS desta marca ainda não foi escrito.</Serif>
      <span className="rr-faint">ele sai do que entra no cliente: a marca, a escuta, o futuro, o mercado, as praças e os dados. peça à Machine as primeiras propostas e revise.</span>
      <div><Button size="sm" variant="primary" onClick={onBuild}>ir para construir</Button></div>
    </div>
  );
  return (
    <div className="rr-bisbook">
      <nav className="rr-bisbook__index" aria-label="capítulos">
        <Label>capítulos</Label>
        {chapters.map((c, n) => <a key={c.section} href={`#bis-${c.section}`}><span>{num(n)}</span>{BIS_SECTION_LABELS[c.section]}<small>{c.items.length}</small></a>)}
        {contexts.length > 0 && (
          <div className="rr-bisbook__filter">
            <Label>onde vale</Label>
            <div>
              <button type="button" className={cx('rr-chip', !where && 'rr-chip--on')} onClick={() => setWhere('')}>tudo</button>
              {contexts.map((c) => <button key={c} type="button" className={cx('rr-chip', where === c && 'rr-chip--on')} onClick={() => setWhere(where === c ? '' : c)}>{c}</button>)}
            </div>
            {where && <span className="rr-faint">mostrando o que vale para {where} e o que vale sempre.</span>}
          </div>
        )}
        {proposals > 0 && active.length > 0 && <button type="button" className="rr-action rr-action--bare" onClick={() => setWithProposals((v) => !v)}>{withProposals ? 'esconder as propostas' : `mostrar ${plural(proposals, 'proposta', 'propostas')}`}</button>}
      </nav>
      <main className="rr-bisbook__pages">
        <section className="rr-bisbook__cover">
          <Label tone="accent">Brand Intelligence System</Label>
          <h1>{name}</h1>
          <p>{d.version ? `versão ${bisV(d)}, publicada em ${formatDate(d.publishedAt!)}.` : 'ainda sem versão publicada.'} {d.pending.total > 0 || proposals > 0 ? 'você está lendo a cópia de trabalho, com o que ainda não foi publicado.' : ''}</p>
          {active.length === 0 && <Notice>tudo aqui ainda é proposta da Machine. nada vale até alguém da Raro aceitar, em <button type="button" className="rr-action" onClick={onBuild}>construir</button>.</Notice>}
        </section>
        {chapters.map((c, n) => (
          <section key={c.section} id={`bis-${c.section}`} className="rr-bisbook__chapter">
            <header><span>{num(n)}</span><h2>{BIS_SECTION_LABELS[c.section]}</h2><p>{BIS_SECTION_HINTS[c.section]}</p></header>
            {c.items.map((i) => (
              <article key={i.id} className={cx('rr-bisbook__block', i.status === 'proposed' && 'rr-bisbook__block--proposed')}>
                <div className="rr-bisbook__blockhead">
                  <h3>{i.title}</h3>
                  <span>{i.status === 'proposed' && <Tag tone="accent">proposta</Tag>}{appliesOf(i).length > 0 && <small>vale para: {appliesOf(i).join(', ')}</small>}</span>
                </div>
                <BlockView block={i} />
              </article>
            ))}
          </section>
        ))}
        {d.live && (
          <p className="rr-bis__live">o BIS também se apoia no que vive na machine e não é copiado para cá: {plural(d.live.competitors, 'concorrente no radar', 'concorrentes no radar')}, {plural(d.live.evidence, 'evidência aceita', 'evidências aceitas')}, {plural(d.live.decisions, 'decisão', 'decisões')} e {plural(d.live.learnings, 'aprendizado', 'aprendizados')}.</p>
        )}
      </main>
    </div>
  );
}

// ───────────────────────── construir ─────────────────────────
function Builder({ d, organizationId, qs, onDone, onConstruction, doc, setDoc }: { d: BisData; organizationId: string; qs: string; onDone: () => void; onConstruction: () => void; doc: string | null; setDoc: (id: string | null) => void }) {
  const first = BIS_SECTIONS.find((s) => d.items.some((i) => i.section === s && i.status === 'proposed')) ?? BIS_SECTIONS.find((s) => d.items.some((i) => i.section === s)) ?? 'identity';
  const [section, setSection] = useState<BisSection>(first);
  const [boards, setBoards] = useState(false);
  const [sel, setSel] = useState<{ id: string | null; d: BlockDraft } | null>(null);
  const [note, setNote] = useState('');
  const [level, setLevel] = useState<BisVersionLevel | null>(null);
  const next = publishLabel(d, level);
  const draft = useMutation({ mutationFn: () => api.post(`/organizations/${organizationId}/bis/draft${qs}`), onSuccess: onDone });
  const acceptAll = useMutation({ mutationFn: () => api.post(`/organizations/${organizationId}/bis/accept-all${qs}`), onSuccess: onDone });
  const patch = useMutation({ mutationFn: (x: { id: string; body: Record<string, unknown> }) => api.patch(`/bis-items/${x.id}`, x.body), onSuccess: onDone });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/bis-items/${id}`), onSuccess: () => { setSel(null); onDone(); } });
  const add = useMutation({ mutationFn: (b: BlockDraft) => api.post(`/organizations/${organizationId}/bis/items${qs}`, sendBlock(b)), onSuccess: () => { setSel(null); onDone(); } });
  const publish = useMutation({ mutationFn: () => api.post(`/organizations/${organizationId}/bis/publish${qs}`, { note, level: next.chosen }), onSuccess: () => { setNote(''); setLevel(null); onDone(); } });
  // segurar como rascunho: a mudança fica na cópia de trabalho e não vai na próxima versão
  const hold = useMutation({ mutationFn: async (x: { ids: string[]; held: boolean }) => { for (const id of x.ids) await api.patch(`/bis-items/${id}`, { held: x.held }); }, onSuccess: onDone });
  const unpublished = d.items.filter((i) => i.state === 'new' || i.state === 'changed' || i.state === 'held');
  const refs = useQuery({ queryKey: ['bis-references', organizationId, qs], queryFn: () => api.get<RefRow[]>(`/organizations/${organizationId}/bis/references${qs}`), refetchInterval: (x) => (x.state.data?.some((r) => r.status === 'reading' || r.status === 'thinking') ? 4000 : false) });
  const picker = useRef<HTMLInputElement>(null);
  const [sending, setSending] = useState<{ name: string; pct: number | null }[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);
  // um arquivo por chamada: vídeo grande não segura os outros, e cada um vira uma conversa própria
  const send = async (list: File[]) => {
    setSendError(null);
    let last: string | null = null;
    for (const file of list) {
      setSending((s) => [...s, { name: file.name, pct: null }]);
      try {
        // arquivo grande vai direto ao armazenamento (o Cloudflare recusa acima de 100 MB pela API); a API só confere o recibo
        const upload = await uploadDirect(organizationId, 'bis-reference', file, (pct) => setSending((s) => s.map((x) => (x.name === file.name ? { ...x, pct } : x))));
        let body: FormData | { upload: string };
        if (upload) body = { upload };
        else { body = new FormData(); body.append('file', file); }
        last = (await api.post<{ id: string }>(`/organizations/${organizationId}/bis/references${qs}`, body)).id;
      } catch (e) { setSendError(`${file.name}: ${(e as Error).message}`); }
      setSending((s) => s.filter((x) => x.name !== file.name));
      void refs.refetch();
    }
    if (last && list.length === 1) setDoc(last);
  };
  const drafting = d.draft.status === 'pending' || draft.isPending;
  const items = d.items.filter((i) => i.section === section);
  const saveError = ((sel?.id ? patch.error : add.error) as Error | null)?.message ?? null;
  const open = (i: BisItem) => { patch.reset(); setSel({ id: i.id, d: draftOf(i) }); };
  const save = () => { if (!sel) return; if (sel.id) patch.mutate({ id: sel.id, body: sendBlock(sel.d) }, { onSuccess: () => setSel(null) }); else add.mutate(sel.d); };

  return (
    <div className="rr-bisbuild">
      <aside className="rr-bisbuild__nav">
        <Label>estrutura</Label>
        {BIS_SECTIONS.map((s, n) => {
          const all = d.items.filter((i) => i.section === s);
          const proposed = all.filter((i) => i.status === 'proposed').length;
          return (
            <button key={s} type="button" className={cx('rr-bisbuild__chapter', section === s && 'rr-bisbuild__chapter--on')} onClick={() => { setSection(s); setSel(null); setDoc(null); }}>
              <span>{num(n)}</span>
              <b>{BIS_SECTION_LABELS[s]}</b>
              <small>{!all.length ? 'vazio' : [all.length - proposed && `${all.length - proposed} em vigor`, proposed && plural(proposed, 'proposta', 'propostas')].filter(Boolean).join(' · ')}</small>
              {proposed > 0 && <i aria-hidden />}
            </button>
          );
        })}

        <div className="rr-bisbuild__box">
          <Label tone="accent">a Machine</Label>
          {drafting ? <Thinking label="rascunhando o BIS" steps={[{ kind: 'stage', text: 'lendo o que já se sabe desta marca e escrevendo as propostas em blocos' }]} lines={3} /> : (
            <>
              {d.draft.error && <Notice tone={d.draft.status === 'failed' ? 'error' : undefined}>{d.draft.error}</Notice>}
              <small>a Machine propõe o BIS na construção, área por área, com a origem de cada coisa.</small>
              {d.draft.basis && d.pending.proposed > 0 && <p><Serif italic>{d.draft.basis.note}</Serif></p>}
              {d.draft.basis && <small>saiu de: {d.draft.basis.parts.join(' · ')}</small>}
              <Button size="sm" variant={d.items.length ? 'quiet' : 'primary'} onClick={onConstruction}>abrir a construção</Button>
              {d.canEdit && d.pending.proposed > 0 && <button type="button" className="rr-action rr-action--bare" disabled={acceptAll.isPending} onClick={() => acceptAll.mutate()}>aceitar as {d.pending.proposed} que restam</button>}
            </>
          )}
        </div>

        <div className="rr-bisbuild__box">
          <Label>documentos de referência</Label>
          <small>manual, apresentação, planilha, áudio de reunião, foto, vídeo. a Machine lê, diz o que diverge, o que complementa e onde cada coisa entra, e pergunta o que não souber.</small>
          {(refs.data ?? []).map((r) => (
            <button key={r.id} type="button" className={cx('rr-bisbuild__chapter', 'rr-bisbuild__doc', doc === r.id && 'rr-bisbuild__chapter--on')} onClick={() => { setSel(null); setDoc(r.id); }}>
              <span aria-hidden>·</span>
              <b>{r.name}</b>
              <small>{refState(r)}</small>
              {(r.open > 0 || r.waiting) && r.status === 'ready' && <i aria-hidden />}
            </button>
          ))}
          {sending.map((x) => <small key={x.name}>enviando {x.name}{x.pct !== null ? ` · ${x.pct}%` : '…'}</small>)}
          {sendError && <small>{sendError}</small>}
          {d.canEdit && (
            <>
              <input ref={picker} type="file" multiple hidden onChange={(e) => { const list = [...(e.target.files ?? [])]; e.target.value = ''; if (list.length) void send(list); }} />
              <Button size="sm" variant="quiet" disabled={sending.length > 0} onClick={() => picker.current?.click()}>subir documento</Button>
            </>
          )}
        </div>

        {d.items.length > 0 && (d.pending.total > 0 || d.pending.held > 0) && (
          <div className="rr-bisbuild__box">
            <Label>{d.version ? `mudanças desde a v${bisV(d)}` : 'ainda não publicado'}</Label>
            <small>{[d.pending.added && plural(d.pending.added, 'bloco novo', 'blocos novos'), d.pending.changed && plural(d.pending.changed, 'alterado', 'alterados'), d.pending.removed && plural(d.pending.removed, 'retirado', 'retirados'), d.pending.held && plural(d.pending.held, 'fica como rascunho', 'ficam como rascunho')].filter(Boolean).join(' · ')}</small>
            {unpublished.length > 0 && (
              <div className="rr-bisbuild__publist">
                {unpublished.map((i) => (
                  <Toggle key={i.id} checked={!i.held} disabled={!d.canEdit || hold.isPending} onChange={(on) => hold.mutate({ ids: [i.id], held: !on })}
                    label={i.title} hint={i.held ? (i.wasPublished ? 'rascunho: segue valendo a versão publicada' : 'rascunho: não entra nesta versão') : `${i.state === 'new' ? 'novo' : 'alterado'} · ${BIS_SECTION_LABELS[i.section]}`} />
                ))}
                {d.canEdit && unpublished.length > 1 && (
                  <span>
                    <button type="button" className="rr-action rr-action--bare" disabled={hold.isPending} onClick={() => hold.mutate({ ids: unpublished.filter((i) => i.held).map((i) => i.id), held: false })}>marcar todos</button>
                    <button type="button" className="rr-action rr-action--bare" disabled={hold.isPending} onClick={() => hold.mutate({ ids: unpublished.filter((i) => !i.held).map((i) => i.id), held: true })}>segurar todos</button>
                  </span>
                )}
              </div>
            )}
            {d.canPublish ? (
              <>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="o que mudou nesta versão e por quê" />
                <PublishLevel d={d} level={level} onChange={setLevel} />
                <Button size="sm" variant="primary" disabled={publish.isPending || note.trim().length < 8 || d.pending.proposed > 0 || d.pending.total === 0} onClick={() => publish.mutate()}>{publish.isPending ? 'publicando…' : `publicar a v${next.label}`}</Button>
                {d.pending.proposed > 0 && <small>responda às propostas antes de publicar.</small>}
                {d.pending.proposed === 0 && d.pending.total === 0 && <small>marque o que vai nesta versão.</small>}
                {publish.isError && <small>{(publish.error as Error).message}</small>}
              </>
            ) : <small>publicar uma versão é decisão de sócio.</small>}
          </div>
        )}
        {d.versions.length > 0 && (
          <div className="rr-bisbuild__box">
            <Label>versões</Label>
            {d.versions.slice(0, 5).map((v) => <small key={v.number}><b>v{v.label ?? v.number}</b> · {formatDate(v.publishedAt)}{v.by ? ` · ${v.by}` : ''}: {v.note}</small>)}
          </div>
        )}
      </aside>

      {doc ? <ReferenceRoom key={doc} organizationId={organizationId} id={doc} canEdit={d.canEdit} onBis={() => { onDone(); void refs.refetch(); }} onClose={() => setDoc(null)} onRemoved={() => { setDoc(null); void refs.refetch(); }} /> : <>
      <main className="rr-bisbuild__list">
        <header>
          <div><h2>{BIS_SECTION_LABELS[section]}</h2><p>{BIS_SECTION_HINTS[section]}</p></div>
          <div className="rr-bisboard__row">
            {d.canEdit && section === 'visual' && <Button size="sm" variant="quiet" onClick={() => { setSel(null); setBoards(true); }}>ler pranchas</Button>}
            {d.canEdit && <Button size="sm" variant="quiet" onClick={() => { add.reset(); setSel({ id: null, d: blankBlock(section) }); }}>+ bloco</Button>}
          </div>
        </header>
        {boards && section === 'visual' && <BoardReader organizationId={organizationId} brandId={new URLSearchParams(qs).get('brandId')} onDone={onDone} onClose={() => setBoards(false)} />}
        {!items.length && <div className="rr-bis__empty"><Serif italic>capítulo vazio.</Serif><span className="rr-faint">a Machine só propõe aqui quando há material que sustente. veja na nota do rascunho o que falta ouvir ou ler.</span></div>}
        {items.map((i) => {
          const missing = missingFields(i.kind ?? 'note', i.data ?? {}, i.ruleKind);
          return (
            <article key={i.id} className={cx('rr-bisbuild__card', i.status === 'proposed' && 'rr-bisbuild__card--proposed', sel?.id === i.id && 'rr-bisbuild__card--on')} onClick={() => open(i)}>
              <div className="rr-bisbuild__cardhead">
                <span className="rr-label">{BIS_BLOCK_LABELS[i.kind ?? 'note']}</span>
                <b>{i.title}</b>
              </div>
              <BlockView block={i} compact />
              <div className="rr-bis__meta">
                {i.state !== 'published' && <Tag tone={i.state === 'proposed' ? 'accent' : 'outline'}>{BIS_STATE_LABELS[i.state]}</Tag>}
                {i.ruleKind === 'compliance' && <Tag tone="outline">conformidade: bloqueia</Tag>}
                <span>{BIS_INFO_CLASS_LABELS[i.infoClass]}</span>
                {appliesOf(i).length > 0 && <span>vale para: {appliesOf(i).join(', ')}</span>}
                {i.basis && <span>de onde vem: {i.basis}</span>}
                {missing.length > 0 && <span>falta: {missing.join(', ')}</span>}
              </div>
              {d.canEdit && (
                <div className="rr-bis__actions" onClick={(e) => e.stopPropagation()}>
                  {i.state === 'proposed' && <button type="button" className="rr-action" disabled={patch.isPending} onClick={() => patch.mutate({ id: i.id, body: { status: 'active' } })}>aceitar</button>}
                  <button type="button" className="rr-action rr-action--bare" onClick={() => open(i)}>{i.state === 'proposed' ? 'reescrever' : 'editar'}</button>
                  {(i.state === 'new' || i.state === 'changed' || i.state === 'held') && <button type="button" className="rr-action rr-action--bare" disabled={hold.isPending} onClick={() => hold.mutate({ ids: [i.id], held: !i.held })}>{i.held ? 'publicar na próxima versão' : 'segurar como rascunho'}</button>}
                  {i.state === 'proposed'
                    ? <button type="button" className="rr-action rr-action--bare" disabled={patch.isPending} onClick={() => patch.mutate({ id: i.id, body: { status: 'rejected' } })}>descartar</button>
                    : <button type="button" className="rr-action rr-action--bare" disabled={remove.isPending} onClick={() => remove.mutate(i.id)}>tirar</button>}
                </div>
              )}
            </article>
          );
        })}
        {d.removed.length > 0 && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>sai na próxima versão: {d.removed.map((r) => r.title).join(' · ')}</p>}
      </main>

      <aside className="rr-bisbuild__side">
        {!sel ? (
          <div className="rr-bisbuild__hint">
            <Label>o bloco</Label>
            <p>escolha um bloco para abrir. aceitar vale como está; reescrever abre aqui os campos do tipo, onde vale e quem lê.</p>
            <p>ninguém preenche o BIS do zero: ele sai do que entra no cliente. o "+ bloco" é para o que a Raro sabe e ainda não está em nenhum material.</p>
          </div>
        ) : !d.canEdit ? <BlockView block={{ ...sel.d, force: sel.d.section === 'rules' ? sel.d.force : null, ruleKind: sel.d.section === 'rules' ? sel.d.ruleKind : null }} /> : (
          <>
            <Label>{sel.id ? 'editar o bloco' : 'novo bloco'}</Label>
            {!sel.id && (
              <MachineFill<{ section: BisSection; title: string; body: string; force: BisForce | null; ruleKind: BisRuleKind | null }>
                form="bis_item" organizationId={organizationId}
                placeholder="diga do seu jeito: a marca nunca promete prazo de atendimento em anúncio"
                onFill={(x) => setSel({ id: null, d: { ...blankBlock(x.section), title: x.title, body: x.body.replace(/\s*\[[^\]]{2,80}\]/g, '').trim(), force: x.force ?? 'should', ruleKind: x.ruleKind ?? 'brand', infoClass: BIS_DEFAULT_CLASS[x.section] } })}
              />
            )}
            <BlockEditor d={sel.d} onChange={(x) => setSel({ id: sel.id, d: x })} onSave={save} onCancel={() => setSel(null)} busy={patch.isPending || add.isPending} isNew={!sel.id} error={saveError} />
          </>
        )}
      </aside>
      </>}
    </div>
  );
}
